// Central runtime configuration.
//
// Every externally-reachable URL / credential the app needs is sourced here from
// Vite env vars (`import.meta.env.VITE_*`), with production defaults as fallback.
// Define the values per-environment in `.env.development` / `.env.production`
// (see `.env.example`). Never read `import.meta.env` for these directly elsewhere —
// import from this module so there is a single source of truth.

const env = import.meta.env

export const config = {
  /** Application backend API base. Empty string => same-origin (Vite dev proxy). */
  apiUrl: env.VITE_API_URL || '',

  /** Phoenix signaling websocket base, e.g. "wss://signal.instavc.com/socket". */
  signalingWsUrl: env.VITE_SIGNALING_WS_URL || 'wss://signal.instavc.com/socket',

  /** SFU orchestrator (media server) configuration. */
  sfu: {
    orchestratorUrl: env.VITE_SFU_URL || 'https://orchestration.instavc.ai',
    apiKey: env.VITE_SFU_API_KEY || '',
  },

  /**
   * Live transcription (speech-to-text) service — a standalone Socket.IO
   * server, separate from the SFU and signaling.
   *
   * No production default: unlike the other services above, there is no
   * stable deployed address for this one yet. An empty value means
   * "not configured", and `useLiveTranscriptionCapture` treats that as a
   * no-op rather than connecting to nothing (the same pattern `noticesUrl`
   * uses for "no page for this yet").
   */
  transcription: {
    serviceUrl: env.VITE_TRANSCRIPTION_SERVICE_URL || '',
  },

  /**
   * H.323 / SIP gateway address advertised in a copied invitation, for room
   * systems dialling in.
   *
   * ⚠️ The default below is PLACEHOLDER data so the dial-in section is visible
   * while the invitation is being reviewed. It is not a verified dial-in
   * address — set `VITE_H323_GATEWAY` per environment before anyone is told to
   * dial it, or clear it to drop the section from the invite entirely.
   */
  h323Gateway: env.VITE_H323_GATEWAY || '13.203.253.156',

  /** WebRTC ICE (STUN/TURN) servers. */
  /**
   * STUN only. TURN credentials are minted per join by the backend
   * (`features/meeting/services/turnCredentials.api`) because the shared coturn
   * uses `use-auth-secret`: the credential is an HMAC of an expiry timestamp, so
   * it cannot be a build-time constant, and the secret must never reach a
   * browser bundle.
   */
  ice: {
    stunUrl: env.VITE_STUN_URL || 'stun:stun.instavc.com:19302',
  },

  /** SecureID OIDC issuer. */
  secureId: {
    issuerUrl: env.VITE_SECUREID_ISSUER_URL || 'https://secureidapi.instavc.ai',
    clientId: env.VITE_SECUREID_CLIENT_ID || '',
  },

  /**
   * The legal / marketing pages the footers link out to.
   *
   * The two policy URLs are copied from what the sign-in and sign-up screens
   * link today — `LoginPage`, `AuthLayout` and `SignupPage` each still hold
   * their own hardcoded copy, so a change here wants making there too until
   * they are moved onto this.
   *
   * An empty value means "no page for this yet", and the label renders as
   * plain text — never as an anchor that goes nowhere, which is what the
   * prejoin footer used to do for all four of these.
   */
  legal: {
    termsUrl: env.VITE_TERMS_URL || 'https://www.instavc.com/terms-and-conditions/',
    privacyUrl: env.VITE_PRIVACY_URL || 'https://inmeet.ai/privacy-policy/',
    /** No such page exists yet — set it per deployment to light the link up. */
    noticesUrl: env.VITE_NOTICES_URL || '',
    learnMoreUrl: env.VITE_LEARN_MORE_URL || 'https://inmeet.ai/',
  },

  /** Public-facing product base URLs used to build shareable links. */
  appBaseUrl: env.VITE_APP_BASE_URL || 'https://devapp.instavc.ai',
  webinarBaseUrl: env.VITE_WEBINAR_BASE_URL || 'https://apps.inmeet.ai',
  recordingBaseUrl: env.VITE_RECORDING_BASE_URL || 'https://meeting.inmeet.ai',
  telepresenceUrl: env.VITE_TELEPRESENCE_URL || 'https://tp.inmeet.ai',

  /**
   * SIP gateway host used to build dial-in addresses (`sip:<meetingCode>@<host>`).
   * MUST be the host that actually answers SIP — the gateway — not a bare
   * product domain. `instavc.com` was a placeholder that resolved nowhere, so
   * every advertised SIP address failed. The H.323 gateway address above and
   * this are the same box today.
   */
  sipDomain: env.VITE_SIP_DOMAIN || 'gateway1.instavc.ai',

  /**
   * Self-hosted whiteboard base, e.g. "https://<host>/whiteboard/".
   *
   * Held per client rather than sent over the wire: the room only broadcasts
   * that a board is open, and each client builds its own framed URL from this
   * base plus the room id and its own identity. So a participant can never
   * hand another participant a link to load.
   *
   * Empty disables the feature — the control is hidden rather than opening a
   * blank frame.
   */
  whiteboardUrl: env.VITE_WHITEBOARD_URL || '',
} as const

// getIceServers() was removed deliberately. It embedded a fixed TURN username
// and password in every build — a permanent relay credential published in a
// public JavaScript bundle. Use buildIceServers() from
// features/meeting/services/turnCredentials.api, which fetches a short-lived
// pair from the backend at join time.
